/**
 * OperationCenterHistoryModal — timeline of every SOC phone edit
 * for a specific country. Reads from the audit trail via
 * ``GET /api/operation-centers/{country_code}/history``.
 *
 * Zorzutti bug follow-up 24/08/2026 — Legal asked "when did that
 * number change and who edited it?" without opening a Mongo shell.
 * This widget answers that in two clicks from the operation-centres
 * admin page.
 */
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  X, Clock, User, ArrowRight, ArrowUpCircle, RotateCcw, Loader2,
  ShieldCheck, AlertCircle, Phone, Eye,
} from "lucide-react";
import { API } from "../../config";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso.slice(0, 16); }
};

const fmtRelative = (iso) => {
  if (!iso) return "";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));
    if (days > 30) return `${Math.floor(days / 30)} ${Math.floor(days / 30) === 1 ? "mese fa" : "mesi fa"}`;
    if (days >= 1) return `${days} ${days === 1 ? "giorno fa" : "giorni fa"}`;
    if (hours >= 1) return `${hours}h fa`;
    if (minutes >= 1) return `${minutes}m fa`;
    return "poco fa";
  } catch { return ""; }
};

const getAuthHeader = () => {
  const token = localStorage.getItem("token") || localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Which UI languages Legal typically wants to preview. Sticks to the
// three biggest active markets to keep the widget compact — Legal can
// still open the full customer wallet if they need FR/DE/ES.
const PREVIEW_LANGS = [
  { code: "it", label: "IT" },
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
];


/**
 * CustomerCardPreview — inline replica of the "Save the Operations
 * Center number" block that customers see at the end of their wallet
 * flow (mirrors CustomerWalletPage.js lines 1522-1578). Uses the
 * LIVE center payload — the same one every customer would receive
 * for that country/language right now.
 *
 * Purpose: close the "I edited → does it look right?" feedback loop
 * without asking Legal to simulate a full contract subscription just
 * to verify the SOC number.
 */
const CustomerCardPreview = ({ preview, langLabel }) => {
  if (!preview) return null;
  const label = preview.label || "Operations Center";
  const callLabel = preview.call || "Chiama";
  const saveNumTitle = preview.save_number || "Salva il numero";
  const saveNumDesc = preview.save_number_desc
    || "Salva questo numero in rubrica per averlo sempre a portata di mano.";

  return (
    <div
      className="rounded-xl border border-lojack-red/20 bg-gradient-to-br from-[#111] to-[#0a0a0a] p-4"
      data-testid={`card-preview-${langLabel.toLowerCase()}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Phone className="text-lojack-red" size={14} />
          <h5 className="text-white text-sm font-semibold">{saveNumTitle}</h5>
        </div>
        <span
          className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 border border-gray-700"
          aria-label={`Preview lingua ${langLabel}`}
        >
          {langLabel}
        </span>
      </div>
      <p className="text-gray-500 text-[11px] leading-relaxed mb-3 line-clamp-2">
        {saveNumDesc}
      </p>
      {/* Faux "Chiama" button — visually the CTA the customer sees.
          We render it as a `div` (not `a href="tel:"`) so a mis-click
          on the admin side does not accidentally start a phone call. */}
      <div
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-lojack-red/10 border border-lojack-red/30 text-lojack-red text-xs font-semibold cursor-default"
        title="Anteprima — non cliccabile"
      >
        <Phone size={13} />
        <span>
          {callLabel}: <span className="font-mono">{preview.phone || "—"}</span>
        </span>
      </div>
      <p className="mt-2 text-[10px] text-gray-600">
        Label mostrata: <span className="text-gray-400">{label}</span>
      </p>
    </div>
  );
};

CustomerCardPreview.propTypes = {
  preview: PropTypes.object,
  langLabel: PropTypes.string.isRequired,
};


export const OperationCenterHistoryModal = ({ countryCode, countryName, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const r = await axios.get(
          `${API}/operation-centers/${countryCode}/history?limit=100`,
          { headers: getAuthHeader() },
        );
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) {
          const detail = e?.response?.data?.detail;
          setError(detail || "Impossibile caricare la cronologia");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [countryCode]);

  const total = data?.entries?.length || 0;

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="soc-history-modal-backdrop"
    >
      <div
        className="bg-[#0a0a0a] border border-gray-800 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="soc-history-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-lojack-red/10 flex items-center justify-center">
              <Clock size={18} className="text-lojack-red" />
            </div>
            <div>
              <h2 className="text-white text-lg font-bold">
                Cronologia modifiche SOC
              </h2>
              <p className="text-gray-500 text-xs uppercase tracking-wider">
                {countryName} · {countryCode}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition"
            data-testid="soc-history-close"
            aria-label="Chiudi"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading && (
            <div className="flex items-center justify-center py-12" data-testid="soc-history-loading">
              <Loader2 size={24} className="animate-spin text-gray-500" />
            </div>
          )}

          {!loading && error && (
            <div
              className="flex items-start gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-sm"
              data-testid="soc-history-error"
            >
              <AlertCircle size={18} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && total >= 0 && data && (
            <div className="mb-5" data-testid="soc-live-preview">
              <div className="flex items-center gap-2 mb-3">
                <Eye size={14} className="text-lojack-blue-400" />
                <p className="text-white text-xs font-semibold uppercase tracking-wider">
                  Anteprima card cliente
                </p>
                <span className="ml-auto text-[10px] text-gray-500">
                  Numero attuale:{" "}
                  <span className="font-mono text-lojack-red">
                    {data.current_phone || "—"}
                  </span>
                </span>
              </div>
              <p className="text-gray-500 text-[11px] leading-relaxed mb-3">
                Come apparirà la card &quot;Salva il numero&quot; ai clienti di{" "}
                <span className="text-gray-300">{countryName}</span> in tempo reale
                &mdash; per le 3 lingue principali.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {PREVIEW_LANGS.map(({ code, label }) => (
                  <CustomerCardPreview
                    key={code}
                    preview={data.live_preview?.[code]}
                    langLabel={label}
                  />
                ))}
              </div>
              <div className="mt-4 h-px bg-gray-800" />
            </div>
          )}

          {!loading && !error && total === 0 && (
            <div
              className="text-center py-8"
              data-testid="soc-history-empty"
            >
              <ShieldCheck size={40} className="text-gray-700 mx-auto mb-3" />
              <p className="text-gray-300 text-sm font-medium mb-1">
                Nessuna modifica registrata
              </p>
              <p className="text-gray-500 text-xs max-w-md mx-auto">
                Il numero SOC per <span className="font-mono text-gray-400">{countryName}</span> non
                è mai stato modificato dall&apos;admin panel. Attualmente in uso il
                valore di default:
              </p>
              <p className="mt-2 font-mono text-lojack-red text-sm">
                {data?.static_default_phone || "—"}
              </p>
            </div>
          )}

          {!loading && !error && total > 0 && (
            <div data-testid="soc-history-timeline">
              <p className="text-gray-500 text-xs mb-4">
                Timeline delle modifiche (più recente in alto) ·{" "}
                <span className="text-gray-400 font-semibold">{total}</span>{" "}
                {total === 1 ? "voce" : "voci"}
              </p>

              {data.entries.map((e, i) => (
                <div key={i} className="relative pl-8 pb-5 last:pb-0" data-testid={`soc-history-entry-${i}`}>
                  {/* Vertical timeline line */}
                  <div className="absolute left-3 top-2 bottom-0 w-px bg-gray-800" />
                  {/* Timeline dot */}
                  {e.action === "phone_revert" ? (
                    <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-gray-700/30 border border-gray-600/40 flex items-center justify-center">
                      <RotateCcw size={11} className="text-gray-300" />
                    </div>
                  ) : (
                    <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-lojack-red/10 border border-lojack-red/30 flex items-center justify-center">
                      <ArrowUpCircle size={11} className="text-lojack-red" />
                    </div>
                  )}

                  <div className="bg-[#111] border border-gray-800 rounded-lg p-3 hover:border-gray-700 transition-colors">
                    {/* Header row */}
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                          e.action === "phone_revert"
                            ? "bg-gray-700/30 text-gray-300 border border-gray-600/40"
                            : "bg-lojack-red/10 text-lojack-red border border-lojack-red/30"
                        }`}>
                          {e.action === "phone_revert" ? (
                            <><RotateCcw size={10} /> Ripristino default</>
                          ) : (
                            <><ArrowUpCircle size={10} /> Modifica</>
                          )}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-white text-[11px] font-medium">{fmtDateTime(e.at)}</div>
                        <div className="text-gray-500 text-[10px]">{fmtRelative(e.at)}</div>
                      </div>
                    </div>

                    {/* Old → New value */}
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="px-2 py-1 rounded bg-red-500/10 border border-red-500/20 text-red-300 text-[11px] font-mono">
                        {e.old_phone || "—"}
                      </span>
                      <ArrowRight size={12} className="text-gray-600 shrink-0" />
                      <span className={`px-2 py-1 rounded text-[11px] font-mono ${
                        e.action === "phone_revert"
                          ? "bg-gray-700/30 border border-gray-600/40 text-gray-200"
                          : "bg-lojack-blue-500/10 border border-lojack-blue-500/20 text-lojack-blue-300"
                      }`}>
                        {e.new_phone || "—"}
                      </span>
                    </div>

                    {/* Actor row */}
                    <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
                      <User size={10} className="opacity-60" />
                      <span className="opacity-70">Modificato da:</span>
                      <span className="text-gray-300 font-mono">{e.actor_email || "—"}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-gray-800 bg-black/30 rounded-b-2xl">
          <p className="text-gray-500 text-[10px] text-center">
            Dati letti dal registro audit ({total} {total === 1 ? "voce" : "voci"}, retention 10 anni · GDPR Art. 5)
          </p>
        </div>
      </div>
    </div>
  );
};

OperationCenterHistoryModal.propTypes = {
  countryCode: PropTypes.string.isRequired,
  countryName: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default OperationCenterHistoryModal;
